/*
 * File: PatientsApp.js
 * Project: mp-mobile-app
 *
 * Created by Jennifer Loecker
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Handles the selection of the patient sidebar item.
 */
import React from "react";
import Router from "components/Router";
import SearchIcon from "@material-ui/icons/Search";
import UIProgress from "components/ui/UIProgress";
import Routes from "./Routes";
import PatientStore from "services/patients/store/PatientStore";
import PatientAlerts from "./alerts/views/AlertsBar";
import PropTypes from "prop-types";
import { StoreContext } from "utils/useStore";
import Preferences from "services/preferences/Store";
import AlertStore from "services/patients/alerts/Store";
import { observer } from "mobx-react";

// these are pages that we do not want to show the
// alerts bar on.  otherwise show them on all patient pages
const hideAlertBarPages = ["alerts", "search"];

class PatientsApp extends React.Component {
  static contextType = StoreContext;

  constructor(props) {
    super(props);
    this.state = { loading: false, loadedPatientId: null };
  }

  checkPatientId() {
    const patientStore = this.context.getStore(PatientStore);
    let patientId = Number(this.props.match.params.patientId);
    if (
      patientId === patientStore?.patientId &&
      this.state.loadedPatientId === patientId
    ) {
      // Already loaded the patient with the patient ID matching the one in the route.
      return;
    }

    if (!patientId && isNaN(patientId)) {
      this.linkToSearch();
    } else {
      patientStore.setPatientId(patientId);
      this.setPatient(patientStore);
    }
  }

  async setPatient(patientStore) {
    const alertStore = this.context.getStore(AlertStore);
    // We want the patient to load first, but we also don't want to show the old alerts between the patient loading and the alerts loading.
    alertStore.invalidate();
    await patientStore.find(this.context.getStore(Preferences));
    await alertStore.fetchAlerts(patientStore.patientId);
    this.setTitleAndSubtitle();
    this.setState({ loading: false, loadedPatientId: patientStore.patientId });
  }

  linkToSearch() {
    this.props.history.push("/search");
  }

  async setTitleAndSubtitle() {
    let rightNav = (
      <div>
        <SearchIcon
          id="search-icon"
          key="searchIcon"
          onClick={() => this.linkToSearch()}
        />
      </div>
    );
    this.props.appStore.setRightNav(rightNav);
    this.props.appStore.setTitle("Patients");
  }

  renderPatientAlerts() {
    // create an array of the url
    let urls = window.location.href.split("/");

    // the url does not contain a page we want to hide the alerts from
    if (!hideAlertBarPages.some(r => urls.includes(r))) {
      return <PatientAlerts />;
    }
  }

  render() {
    // Make sure we've loaded the patient with the same ID as in the current URL.
    this.checkPatientId();
    let { loading } = this.state;
    return (
      <React.Fragment>
        {loading && <UIProgress />}
        {!loading && (
          <React.Fragment>
            {this.renderPatientAlerts()}
            <Router routes={Routes} reRender={() => this.reRender} />
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }
}

PatientsApp.propTypes = {
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  appStore: PropTypes.object.isRequired
};

export default observer(PatientsApp);
